import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Purpose, Satisfaction, and Meaning',
    content: [
      '<strong>Overview:</strong> This module explores how to find and cultivate purpose in life–discovering what gives your life meaning and satisfaction.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Finding personal purpose and meaning',
      '• Cultivating life satisfaction',
      '• Balancing different life domains',
      '<br/>',
      '<strong>Discussion:</strong> Purpose gives direction to your life. When you have a clear sense of purpose, decisions become easier and life feels more meaningful.'
    ]
  },
  {
    title: 'Finding Personal Purpose and Meaning',
    content: [
      'Purpose is the "why" behind your actions–the reason you get up in the morning and the driving force in your life.',
      '<br/>',
      '<strong>Signs You\'ve Found Your Purpose:</strong>',
      '• <strong>Flow State:</strong> Activities that make you lose track of time',
      '• <strong>Energy Boost:</strong> Things that energize rather than drain you',
      '• <strong>Authenticity:</strong> Activities that feel like "being yourself"',
      '• <strong>Impact:</strong> Ways you want to contribute to the world',
      '<br/>',
      '<strong>Common Sources of Purpose:</strong>',
      '• <strong>Relationships:</strong> Building meaningful connections',
      '• <strong>Contribution:</strong> Making a positive difference',
      '• <strong>Growth:</strong> Learning and personal development',
      '• <strong>Creativity:</strong> Expressing yourself through art, music, writing',
      '• <strong>Service:</strong> Helping others or your community',
      '<br/>',
      '<strong>The Purpose Discovery Process:</strong>',
      '1. <strong>Reflect:</strong> What activities make you feel most alive?',
      '2. <strong>Explore:</strong> Try new experiences to discover passions',
      '3. <strong>Connect:</strong> Link your daily actions to something larger',
      '4. <strong>Align:</strong> Ensure your goals support your purpose'
    ]
  },
  {
    title: 'Cultivating Life Satisfaction',
    content: [
      'Life satisfaction comes from balancing different domains and finding joy in the present moment.',
      '<br/>',
      '<strong>Key Life Domains to Balance:</strong>',
      '• <strong>Career/Work:</strong> Meaningful contribution and growth',
      '• <strong>Relationships:</strong> Quality connections with others',
      '• <strong>Health:</strong> Physical and mental well-being',
      '• <strong>Personal Growth:</strong> Learning and self-improvement',
      '• <strong>Leisure:</strong> Rest, hobbies, and fun activities',
      '• <strong>Spirituality:</strong> Connection to something greater',
      '<br/>',
      '<strong>Building Satisfaction in Each Domain:</strong>',
      '• <strong>Assess Current State:</strong> Rate your satisfaction in each area (1-10)',
      '• <strong>Identify Gaps:</strong> Where do you feel most dissatisfied?',
      '• <strong>Set Small Goals:</strong> What one change could improve each domain?',
      '• <strong>Celebrate Progress:</strong> Acknowledge improvements regularly',
      '<br/>',
      '<strong>Daily Practices for Satisfaction:</strong>',
      '• <strong>Gratitude:</strong> Notice what\'s going well each day',
      '• <strong>Mindfulness:</strong> Stay present in enjoyable moments',
      '• <strong>Acts of Kindness:</strong> Small ways to contribute to others',
      '• <strong>Progress Tracking:</strong> Celebrate small wins and growth'
    ]
  },
  {
    title: 'Balancing Different Life Domains',
    content: [
      'True satisfaction comes from harmony across all life areas, not excellence in just one domain.',
      '<br/>',
      '<strong>Tools for Purpose and Meaning:</strong>',
      '• <strong>Life Domain Assessment:</strong> Evaluate satisfaction in key areas (career, relationships, health, etc.)',
      '• <strong>Personal Values Clarification:</strong> Identify what matters most to you',
      '• <strong>Goal Alignment:</strong> Ensure goals support your purpose',
      '<br/>',
      '<strong>Weekly Balance Check:</strong>',
      '• <strong>Monday:</strong> Review the past week\'s balance',
      '• <strong>Wednesday:</strong> Adjust priorities if needed',
      '• <strong>Friday:</strong> Plan for satisfying weekend activities',
      '• <strong>Sunday:</strong> Reflect on overall life satisfaction',
      '<br/>',
      '<strong>Practical Activity:</strong> Reflect on what gives your life meaning. Write about activities that make you lose track of time or feel most alive.',
      '<br/>',
      '<strong>Reflection Questions:</strong>',
      '• What activities make you feel most energized and alive?',
      '• How do your daily actions align with your sense of purpose?',
      '• Which life domains need more attention for better balance?',
      '• What one change could significantly increase your life satisfaction?'
    ],
    additionalSection: {
      title: 'Takeaway',
      content: [
        'Living with purpose means aligning your daily actions with what truly matters to you–creating a life of meaning and satisfaction. Purpose isn\'t a destination but a direction that guides your choices and gives your life coherence.',
        '<br/>',
        '<strong>Remember:</strong> You don\'t need to have everything figured out. Start by identifying what brings you joy and meaning, then build your life around those elements. Small, consistent actions toward purpose create lasting satisfaction.'
      ]
    }
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 5: Purpose, Satisfaction, and Meaning! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about finding purpose and cultivating life satisfaction.',
        'The quiz will help you review key concepts about living a meaningful life.'
      ]
    }
  }
];

const EmotioPsychoRationalModule5Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('emotioPsychoRationalM5');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/emotio-psycho-rational-module-5/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/emotio-psycho-rational-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div 
          className="module-left emotio-psycho-rational-panel"
        >
          <div className="module-header">
            <h1>Module 5</h1>
            <p className="module-subtitle">Purpose, Satisfaction, and Meaning</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Discover what gives your life meaning and learn to cultivate lasting satisfaction across all life domains.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content"> 
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title"> 
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>
            
            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/emotio-psycho-rational-module-5/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmotioPsychoRationalModule5Page;


